import { LexiconSlider } from '@dreamstack/feature-components/src/keenSlider/LexiconSlider/LexiconSlider'
import { MainLayout } from '@dreamstack/feature-components/src/layout/MainLayout'
import { BodyText, HeadingH2 } from '@dreamstack/feature-components/src/layout/Texts'
import { lexiconOtherLocaleUrls } from '@dreamstack/feature-components/src/lexicon/getLexiconPath'
import { LexiconHeaderWithSearch } from '@dreamstack/feature-components/src/lexicon/LexiconHeaderWithSearch'
import {
  getStaticLexiconProps,
  StaticLexiconProps,
} from '@dreamstack/feature-components/src/lexicon/getStaticLexiconProps'
import {
  OtherLocaleUrlsContextProvider,
  useTranslation,
} from '@dreamstack/i18n'
import { SearchContextProvider } from '@dreamstack/search'
import { DreamstackSeo } from '@dreamstack/seo'
import { cn } from '@dreamstack/util/src/cn'
import Image from 'next/image'
import { getStaticPropsPlus } from '../../../lib/getStaticPropsPlus'

type TwoColumnContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  imageOverText?: boolean
}
const TwoColumnContainer: React.FC<TwoColumnContainerProps> = ({
  imageOverText,
  className,
  children,
  ...rest
}) => (
  <div
    className={cn(
      'flex sm:flex-row sm:space-x-px-48 sm:space-y-0',
      imageOverText ? 'flex-col-reverse' : 'flex-col',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const Column: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => (
  <div
    className={cn(
      'flex-1 flex flex-col max-w-full text-left mt-px-32 sm:mt-0',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)
const ImageWrapper: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => (
  <div
    className={cn(
      'h-[208px] md:h-[246px] ml:h-[408px] relative col-start-1 col-span-7',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)
const BottomTextsContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => (
  <div className={cn('mt-px-16 md:mt-px-24 ml:mt-px-32', className)} {...rest}>
    {children}
  </div>
)

export function PropertyLexicon({
  lettersWithContent,
}: Partial<StaticLexiconProps>) {
  const t = useTranslation()
  const title = t('accentro:propertyLexicon.title')

  return (
    <>
      <SearchContextProvider indexType={'lexicon'}>
        <OtherLocaleUrlsContextProvider context={lexiconOtherLocaleUrls}>
          <DreamstackSeo title={title} />
          <MainLayout
            navbarProps={{ forceDarkTextColor: true }}
            showContactFooter={true}
            showNewsletterFooter={true}
          >
            <LexiconHeaderWithSearch />
            {/* content under searchbar */}
            <TwoColumnContainer imageOverText>
              <Column>
                <BodyText className="font-semibold">
                  {t('accentro:propertyLexicon.semiBoldText')}
                </BodyText>
                <BodyText className="mt-px-16">
                  {t('accentro:propertyLexicon.paragraphTexts.paragraphText1')}
                </BodyText>
              </Column>
              <Column>
                <ImageWrapper>
                  <Image
                    src="/static/images/lexicon-landing.png"
                    alt="Couple in apartment"
                    fill
                    style={{ objectFit: 'cover' }}
                  />
                </ImageWrapper>
              </Column>
            </TwoColumnContainer>
            <LexiconSlider lettersWithContent={lettersWithContent || []} />
            {/* content under slider */}
            <div className="mt-px-64 md:mt-px-96">
              <TwoColumnContainer>
                <Column>
                  <HeadingH2>
                    {t('accentro:propertyLexicon.subHeadings.subHeading1')}
                  </HeadingH2>
                  <BottomTextsContainer>
                    <BodyText>
                      {t(
                        'accentro:propertyLexicon.paragraphTexts.paragraphText2'
                      )}
                    </BodyText>
                  </BottomTextsContainer>
                </Column>
                <Column className="mt-px-64 md:mt-0">
                  <HeadingH2>
                    {t('accentro:propertyLexicon.subHeadings.subHeading2')}
                  </HeadingH2>
                  <BottomTextsContainer>
                    <BodyText>
                      {t(
                        'accentro:propertyLexicon.paragraphTexts.paragraphText2'
                      )}
                    </BodyText>
                  </BottomTextsContainer>
                </Column>
              </TwoColumnContainer>
            </div>
          </MainLayout>
        </OtherLocaleUrlsContextProvider>
      </SearchContextProvider>
    </>
  )
}

export const getStaticProps = getStaticPropsPlus({
  getProps: getStaticLexiconProps,
})

export default PropertyLexicon
